import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  TextField,
  ListItemButton,
  InputAdornment,
  CircularProgress,
  TextFieldProps,
} from "@mui/material";
import { ReactElement, useState, useEffect } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { UserSchemaOutput } from "@stw/yup-validators";

import {
  ListSubaccountUsersQuery,
  ListSubaccountUsersQueryVariables,
  UserSortBy,
} from "~frontend/__generated__/graphql";
import { LIST_SUBACCOUNT_USERS } from "~frontend/graphql/users";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useDebounce from "~frontend/util/useDebounce";

const EMPTY_USER_LIST = [{ userId: "", fullName: "" }];

export function UserListSelector<T extends FieldValues>(
  props: TextFieldProps & UseControllerProps<T> & { bottomLabel?: string }
): ReactElement {
  const { subaccount } = useSubaccount()!;
  const [options, setOptions] = useState(EMPTY_USER_LIST);
  const [noOptionsText, setNoOptionsText] = useState("");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const { name, control, label } = props;
  const { field, fieldState } = useController({
    control,
    name,
  });

  const { data, loading, error } = useQuery<
    ListSubaccountUsersQuery,
    ListSubaccountUsersQueryVariables
  >(LIST_SUBACCOUNT_USERS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        sort: UserSortBy.FullNameDesc,
        limit: 10,
        filter: { nameOrEmailContains: debouncedSearch },
      },
      subaccountId: subaccount.id,
    },
  });

  useEffect(() => {
    if (!loading && !error) {
      const newOptions =
        data?.listSubaccountUsers?.users?.map(u => ({
          userId: u.id,
          fullName: u.fullName,
        })) || [];
      setNoOptionsText(newOptions.length === 0 ? "No users found" : "");
      setOptions(newOptions);
    } else if (error) {
      setNoOptionsText("Error searching for user");
      setOptions(EMPTY_USER_LIST);
    }
  }, [data, loading, error]);

  const uiLoading =
    search.length >= 3 && (loading || debouncedSearch !== search);
  return (
    <Autocomplete<UserSchemaOutput>
      value={field.value}
      inputValue={search}
      onChange={(_, value) => {
        field.onChange({
          userId: value?.userId ?? "",
          fullName: value?.fullName ?? "",
        });
        setSearch("");
      }}
      options={options}
      noOptionsText={noOptionsText}
      onInputChange={(event, newSearch) => {
        if (event?.type === "change" || event?.type === "blur") {
          setSearch(newSearch);
        }
      }}
      renderOption={(props, option) => (
        <ListItemButton
          key={option.userId}
          {...(props as unknown as React.HTMLAttributes<HTMLDivElement>)}
        >
          {option.fullName}
        </ListItemButton>
      )}
      getOptionLabel={option => option.fullName ?? ""}
      getOptionKey={option => option.userId}
      isOptionEqualToValue={(option, value) => option.userId === value.userId}
      renderValue={v => v.fullName}
      renderInput={params => (
        <TextField
          label={label}
          error={Boolean(fieldState.error)}
          helperText={
            fieldState.error
              ? (fieldState.error as unknown as { userId: { message: string } })
                  .userId.message
              : props.bottomLabel
          }
          inputRef={field.ref}
          fullWidth
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment
                    position="start"
                    sx={{ padding: theme.spacing(1) }}
                  >
                    <Search />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {uiLoading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      sx={{ marginRight: "64px" }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              sx: {
                border: `1px solid ${palette.midGray}`,
                fontSize: "14px",
                padding: !fieldState.error ? "2.5px !important" : null,
              },
              inputProps: {
                ...params.inputProps,
                sx: {
                  resize: "none",
                  padding: "7.5px 4px 7.5px 5px",
                  marginRight: "50px",
                },
              },
            },
          }}
        />
      )}
    />
  );
}
